import React, { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Dropdown,
  DatePicker,
  Modal,
  Notification,
  NotificationVariants,
  Iconography,
  IconographySizes,
} from "@hid-galaxy-ui/galaxy-react";
import { useAppDispatch, useAppSelector } from "../../../../../../../../redux";
import { 
  saveValidationDocumentationSagaAction,
  submitValidationDocumentationSagaAction,
  getValidationHistorySagaAction,
  getRecentHistoryDocumentationSagaAction
} from "../../../../../../../../redux/saga/validation-documentation/saga-actions";
import {
  getHistoryEnvironmentDetailsSagaAction,
} from "../../../../../../../../redux/saga/project-validation/saga-actions";
import CategorySidebar from "../CategorySidebar/index";
import DocumentationTable from "../DocumentationTable/index";
import ActionButtons from "../ActionButtons/index";
import FilesSection from "../FilesSection/index";
import NotesSection from "../NotesSection/index";
import GuidanceModal from "../modals/GuidanceModal";
import ValidationSummary from "../ValidationSummary/index";
import EnvironmentDetailsModal from "../modals/EnvironmentDetailsModal";
import ConfirmationModal from "../modals/ConfirmationModal";
import ValidationErrorModal from "../modals/ValidationErrorModal";
import sanitizeHtml from "sanitize-html";
import {
  usePartnerSessionCanAccessModule,
  usePartnerSessionCanEditModule,
  usePartnerSessionModuleReadOnly,
} from "../../../../../../../../hooks/usePartnerSessionPermissions";
import { PARTNER_PORTAL_PERMISSION_KEYS as PP_KEYS } from "../../../../../../../../utils/permissions/partnerPortalPermissionKeys";
import { getDocumentationIds } from "../../../../../../../../utils/getDocumentationIds";
import "./ValidationTab.scss";

const sanitizeNotesForApi = (html: string): string =>
  sanitizeHtml(html || "", {
    allowedTags: [
      "p",
      "br",
      "ul",
      "ol",
      "li",
      "strong",
      "b",
      "em",
      "i",
      "u",
      "s",
      "h1",
      "h2",
      "h3",
      "h4",
      "h5",
      "h6",
      "a",
    ],
    allowedAttributes: {
      a: ["href", "target", "rel"],
    },
    allowedSchemes: ["http", "https", "mailto", "tel"],
    transformTags: {
      a: sanitizeHtml.simpleTransform("a", {
        target: "_blank",
        rel: "noopener noreferrer",
      }),
    },
  }).trim();

interface ValidationTabProps {
  isHIDUser: boolean;
  integrationId?: string;
  isDevelopmentCompleted?: boolean;
}

// ========== TEMPORARY: Data entry partner view - start ==========
// Allow specific emails to see partner validation footer and data-entry block on validation screen.
// Remove this block when no longer needed (see comments below and "TEMPORARY: end").
const DATA_ENTRY_PARTNER_VIEW_EMAILS = [
  "nithin.george@hidglobal.com",
  "swetha.patnam@hidglobal.com",
  "chris.mclean@hidglobal.com",
  "andrew.vu@hidglobal.com"
];

const isDataEntryUserEmail = (email?: string | null): boolean =>
  !!email && DATA_ENTRY_PARTNER_VIEW_EMAILS.includes(email.toLowerCase());
// ========== TEMPORARY: Data entry partner view - end ==========

const ValidationTab: React.FC<ValidationTabProps> = ({
  isHIDUser,
  integrationId,
  isDevelopmentCompleted = true,
}) => {
  const dispatch = useAppDispatch();
  const { integrationDetail, projectDetails } = useAppSelector(
    (state) => state.projects
  );
  const { documentationData, historyData, currentDocumentationId, currentValidationStatus, recentHistoryDocumentation, status } = useAppSelector(
    (state) => state.validation_documentation
  );
  const { loggedUserDetail } = useAppSelector((state) => state.auth);
  const { partnerID, projectName } = useParams();

  // Fetch validation history when integrationId is available
  useEffect(() => {
    if (integrationId) {
      dispatch(getValidationHistorySagaAction(Number(integrationId)));
    }
  }, [integrationId, dispatch]);

  // Fetch recent history documentation when historyData has entries
  // This is used both for new version pre-fill and to show last submitted values after admin processing
  useEffect(() => {
    if (
      integrationId &&
      historyData.length > 0
    ) {
      const recentHistoryDocumentationId = historyData[0].documentation_id;
      dispatch(
        getRecentHistoryDocumentationSagaAction(
          Number(integrationId),
          recentHistoryDocumentationId
        )
      );
      dispatch(
        getHistoryEnvironmentDetailsSagaAction({
          integration_id: Number(integrationId),
          documentation_id: recentHistoryDocumentationId,
        })
      );
    }
  }, [integrationId, currentDocumentationId, historyData, dispatch]);

  // Get technology and integration from integrationDetail
  const { technology, integrationName, integrationData } = useMemo(() => {
    if (integrationDetail) {
      return {
        technology: integrationDetail.technology || "",
        integrationName: integrationDetail.integration || "",
        integrationData: integrationDetail,
      };
    }
    return {
      technology: "",
      integrationName: "",
      integrationData: null,
    };
  }, [integrationDetail]);

  const documentationIds = useMemo(
    () =>
      getDocumentationIds(projectDetails, integrationId, integrationDetail),
    [projectDetails, integrationId, integrationDetail]
  );

  // Extract dynamic categories from API response
  const categories = useMemo(() => {
    if (!documentationData || documentationData.length === 0) {
      return [];
    }
    return documentationData.map((cat) => cat.category_name);
  }, [documentationData]);

  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState<string>("");
  const [isGuidanceModalOpen, setIsGuidanceModalOpen] = useState(false);
  const [isSummaryModalOpen, setIsSummaryModalOpen] = useState(false);
  const [isEnvironmentModalOpen, setIsEnvironmentModalOpen] = useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [isValidationErrorModalOpen, setIsValidationErrorModalOpen] = useState(false);
  const [isEnvironmentIncompleteModalOpen, setIsEnvironmentIncompleteModalOpen] = useState(false);
  const [pendingCategories, setPendingCategories] = useState<string[]>([]);
  const [notes, setNotes] = useState("");
  const notesInitializedRef = useRef(false);
  const [documentationFormData, setDocumentationFormData] = useState<
    Record<
      string,
      {
        implementationStatus: string | null;
        verificationStatus: string | null;
        partnerComments: string;
        hidComments: string;
      }
    >
  >({});
  // 2026-03-10: Ref to run seed-from-recent-history only once; prevents partner resubmit from resetting unvisited categories.
  const formDataSeededFromHistoryRef = useRef(false);
  const [toasts, setToasts] = useState<any[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ========== TEMPORARY: Data entry block state (remove with TEMPORARY block) ==========
  const [dataEntryDropdownSelected, setDataEntryDropdownSelected] = useState<{
    label: string;
    value: string;
  } | null>(null);
  const [dataEntrySubmittedOn, setDataEntrySubmittedOn] = useState<Date | null>(null);
  // Partner name dropdown: options from GET /user/listPartnerUser (temporary)
  const [dataEntryPartnerOptions, setDataEntryPartnerOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [dataEntryPartnerOptionsLoading, setDataEntryPartnerOptionsLoading] = useState(false);
  const showDataEntryAndFooter =
    isDataEntryUserEmail(loggedUserDetail?.email);
  const canAccessSubmission = usePartnerSessionCanAccessModule(
    PP_KEYS.VALIDATION_SUBMISSION
  );
  const canEditSubmission = usePartnerSessionCanEditModule(
    PP_KEYS.VALIDATION_SUBMISSION
  );
  const submissionReadOnly = usePartnerSessionModuleReadOnly(
    PP_KEYS.VALIDATION_SUBMISSION
  );
  const canEditApproval = usePartnerSessionCanEditModule(
    PP_KEYS.VALIDATION_APPROVAL
  );
  const approvalReadOnly = usePartnerSessionModuleReadOnly(
    PP_KEYS.VALIDATION_APPROVAL
  );
  const showSubmittedOnDatePicker = showDataEntryAndFooter || isHIDUser;
  const submissionFieldsLocked = submissionReadOnly || !canEditSubmission;
  const approvalFieldsLocked = approvalReadOnly || !canEditApproval;
  const submittedOnLocked = showDataEntryAndFooter
    ? submissionFieldsLocked
    : approvalFieldsLocked;
  // ========== TEMPORARY: Data entry partner notes behaviour - start ==========
  // For notes behaviour: treat data-entry users like partners (use partner_notes instead of hid_notes).
  // When reverting data-entry changes, remove isPartnerNotesUser and revert usages back to isHIDUser-based checks.
  const isPartnerNotesUser = !isHIDUser || showDataEntryAndFooter;
  const notesReadOnly = isPartnerNotesUser
    ? submissionFieldsLocked
    : approvalFieldsLocked;
  // ========== TEMPORARY: Data entry partner notes behaviour - end ==========
  // ========== TEMPORARY: end ==========

  // ========== TEMPORARY: Fetch partner users for Partner name dropdown (GET /user/listPartnerUser/:partnerId). Remove with TEMPORARY block. ==========
  useEffect(() => {
    if (!showDataEntryAndFooter || !partnerID) return;
    let cancelled = false;
    setDataEntryPartnerOptionsLoading(true);
    (async () => {
      try {
        const { axiosRestInstance } = await import("../../../../../../../../common/axios");
        const response = await axiosRestInstance.get<{
          success?: boolean;
          message?: string;
          data?: Array<{ ID: number; email: string; role?: string }>;
        }>(`/user/listPartnerUser/${partnerID}`);
        if (cancelled) return;
        const list = response?.data?.data ?? [];
        // value = user ID so we can send it in save/submit payload when partner is selected (TEMPORARY)
        const options = (Array.isArray(list) ? list : []).map((user) => ({
          label: user.email || String(user.ID),
          value: String(user.ID),
        }));
        setDataEntryPartnerOptions(options);
      } catch (err) {
        if (!cancelled) setDataEntryPartnerOptions([]);
      } finally {
        if (!cancelled) setDataEntryPartnerOptionsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showDataEntryAndFooter, partnerID]);
  // ========== TEMPORARY: end ==========

  // Set activeCategory to first category when categories are loaded
  useEffect(() => {
    if (categories.length > 0 && !activeCategory) {
      setActiveCategory(categories[0]);
    }
  }, [categories, activeCategory]);

  // 2026-03-10: Seed form data from recent history for ALL requirements so unvisited categories keep previous Implementation/Verification/Partner/HID values on partner resubmit.
  useEffect(() => {
    if (
      !documentationData?.length ||
      !recentHistoryDocumentation?.length ||
      formDataSeededFromHistoryRef.current
    ) {
      return;
    }
    const recentMap = new Map<
      number,
      { implementation_status?: string; verification_status?: string; partner_comments?: string; hid_comments?: string }
    >();
    recentHistoryDocumentation.forEach((cat) => {
      cat.requirements?.forEach((r) => {
        recentMap.set(r.requirement_id, {
          implementation_status: r.implementation_status,
          verification_status: r.verification_status,
          partner_comments: r.partner_comments,
          hid_comments: r.hid_comments,
        });
      });
    });
    const seeded: typeof documentationFormData = {};
    documentationData.forEach((cat) => {
      cat.requirements?.forEach((req) => {
        const rowId = `req-${req.requirement_id}`;
        const recent = recentMap.get(req.requirement_id);
        const impl = recent?.implementation_status ?? req.implementation_status ?? null;
        const rawVerif = recent?.verification_status ?? req.verification_status ?? null;
        const verif = rawVerif === "Completed" ? "Pass" : rawVerif;
        seeded[rowId] = {
          implementationStatus: impl || null,
          verificationStatus: (verif as string) || null,
          partnerComments: recent?.partner_comments ?? req.partner_comments ?? "",
          hidComments: recent?.hid_comments ?? req.hid_comments ?? "",
        };
      });
    });
    formDataSeededFromHistoryRef.current = true;
    setDocumentationFormData((prev) => ({ ...seeded, ...prev }));
  }, [documentationData, recentHistoryDocumentation]);

  // Load notes from API data when documentationData is available (only once)
  useEffect(() => {
    if (documentationData && documentationData.length > 0 && !notesInitializedRef.current) {
      // Get notes from first category (notes are now top-level and shared)
      const firstCategory = documentationData[0];
      // TEMPORARY (data entry): data-entry users should read partner_notes even if they are HID users.
      const apiNotes = isPartnerNotesUser
        ? (firstCategory.partner_notes || "")
        : (firstCategory.hid_notes || "");
      
      setNotes(apiNotes);
      notesInitializedRef.current = true;
    }
  }, [documentationData, isPartnerNotesUser]);

  // Track save/submit completion and show toast notifications
  useEffect(() => {
    if ((isSaving || isSubmitting) && !status.documentationLoading) {
      if (!status.documentationError) {
        // Operation was successful
        const successToast = {
          id: Math.floor(Math.random() * 1000),
          title: "",
          message: isSubmitting 
            ? "Validation submitted successfully!" 
            : "Changes saved successfully!",
          variant: NotificationVariants.Success,
          icon: <Iconography icon="circleCheck" size={IconographySizes.Medium} />,
        };
        setToasts([successToast]);
      } else {
        // Operation failed - show error notification
        const errorToast = {
          id: Math.floor(Math.random() * 1000),
          title: "",
          message: status.documentationError || (isSubmitting ? "Failed to submit validation" : "Failed to save changes"),
          variant: NotificationVariants.Error,
          icon: <Iconography icon="ban" size={IconographySizes.Medium} />,
        };
        setToasts([errorToast]);
      }
      setIsSaving(false);
      setIsSubmitting(false);
    }
  }, [status.documentationLoading, status.documentationError, isSaving, isSubmitting]);


  const handleCategorySelect = (category: string) => {
    setActiveCategory(category);
  };

  // Helper function to build save payload
  const buildSavePayload = (includeValidationStatus?: string) => {
    if (
      !documentationIds ||
      !integrationId ||
      !loggedUserDetail?.ID ||
      !documentationData ||
      documentationData.length === 0
    ) {
      return null;
    }

    // Always include ALL categories and ALL requirements (same for both Save and Submit)
    // This ensures the Save API receives the full documentation state, not just changed items
    const categoriesToSave = documentationData
      .map((category) => {
        const categoryRequirements = category.requirements || [];
        const requirementsToSave = categoryRequirements
          .map((req) => {
            const rowId = `req-${req.requirement_id}`;
            const formDataForRequirement = documentationFormData[rowId];

            const currentImplementationStatus =
              formDataForRequirement?.implementationStatus ??
              req.implementation_status ??
              null;
            const currentVerificationStatus =
              formDataForRequirement?.verificationStatus ??
              req.verification_status ??
              null;
            const currentPartnerComments =
              formDataForRequirement?.partnerComments ?? req.partner_comments ?? "";
            const currentHidComments =
              formDataForRequirement?.hidComments ?? req.hid_comments ?? "";

            const requirementPayload: {
              requirement_id: number;
              implementation_status?: string;
              verification_status?: string;
              partner_comments?: string;
              hid_comments?: string;
            } = {
              requirement_id: req.requirement_id,
            };

            requirementPayload.implementation_status =
              currentImplementationStatus || "Not implemented";
            requirementPayload.verification_status =
              (currentVerificationStatus === "Pass" ? "Completed" : currentVerificationStatus) || "Pending";
            requirementPayload.partner_comments = currentPartnerComments;
            requirementPayload.hid_comments = currentHidComments;

            return requirementPayload;
          });

        return {
          category_id: category.category_id,
          requirements: requirementsToSave,
        };
      });

    // Check if notes have changed
    const currentNotes = notes || "";
    let notesChanged = false;
    let originalNotes = "";

    if (documentationData.length > 0) {
      // Get original notes from first category (since notes are now top-level)
      const firstCategory = documentationData[0];
      // TEMPORARY (data entry): use isPartnerNotesUser so data-entry emails compare against partner_notes.
      originalNotes = isPartnerNotesUser
        ? (firstCategory.partner_notes || "")
        : (firstCategory.hid_notes || "");
      notesChanged = sanitizeNotesForApi(currentNotes) !== sanitizeNotesForApi(originalNotes);
    }

    // Build single save payload
    const savePayload: {
      technology: string;
      integration_type: string;
      integration_id: number;
      user: number;
      partner_notes?: string;
      hid_notes?: string;
      validation_status?: string;
      categories: typeof categoriesToSave;
      isHistoryView?: boolean;
      created_at?: string; // TEMPORARY: data-entry "Submitted on". Remove with TEMPORARY block.
    } = {
      technology: documentationIds.technologyId,
      integration_type: documentationIds.integrationTypeId,
      integration_id: Number(integrationId),
      user: loggedUserDetail.ID,
      categories: categoriesToSave,
      isHistoryView: false,
    };

    // Add top-level notes (always include for submit, or if changed for save); preserve safe RTE markup for API
    if (includeValidationStatus || notesChanged) {
      const notesHtml = sanitizeNotesForApi(currentNotes);
      // TEMPORARY (data entry): data-entry users save to partner_notes.
      if (isPartnerNotesUser) {
        savePayload.partner_notes = notesHtml;
      } else {
        savePayload.hid_notes = notesHtml;
      }
    }

    // Add validation_status if provided (for submit)
    if (includeValidationStatus) {
      savePayload.validation_status = includeValidationStatus;
    }

    return savePayload;
  };

  // TEMPORARY: Format Date to YYYY-MM-DD for created_at in save/submit APIs. Remove with TEMPORARY block.
  const formatDataEntryDateToYYYYMMDD = (d: Date | null): string | undefined => {
    if (!d) return undefined;
    // Keep selected calendar day stable across timezone conversions.
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 10);
  };

  // Check if any requirement or notes have changed (for Save flow - skip API call when nothing changed)
  const hasDocumentationChanges = (): boolean => {
    if (!documentationData || documentationData.length === 0) return false;
    if (showSubmittedOnDatePicker && dataEntrySubmittedOn) return true;

    const currentNotes = notes || "";
    const firstCategory = documentationData[0];
    const originalNotes = isHIDUser
      ? (firstCategory.hid_notes || "")
      : (firstCategory.partner_notes || "");
    if (sanitizeNotesForApi(currentNotes) !== sanitizeNotesForApi(originalNotes)) return true;

    for (const category of documentationData) {
      const categoryRequirements = category.requirements || [];
      for (const req of categoryRequirements) {
        const rowId = `req-${req.requirement_id}`;
        const formData = documentationFormData[rowId];
        const currImpl = formData?.implementationStatus ?? req.implementation_status ?? null;
        const currVerif = formData?.verificationStatus ?? req.verification_status ?? null;
        const currPartner = formData?.partnerComments ?? req.partner_comments ?? "";
        const currHid = formData?.hidComments ?? req.hid_comments ?? "";
        const origImpl = req.implementation_status ?? null;
        const origVerif = req.verification_status ?? null;
        const origPartner = req.partner_comments ?? "";
        const origHid = req.hid_comments ?? "";
        if (currImpl !== origImpl || currVerif !== origVerif || currPartner !== origPartner || currHid !== origHid) {
          return true;
        }
      }
    }
    return false;
  };

  // Unified function to push implementation and verification data to backend
  // Used by both save and submit operations
  const pushValidationDataToBackend = (isSubmit: boolean = false) => {
    if (!integrationId || !loggedUserDetail?.ID) {
      console.error("Missing required data for operation");
      return false;
    }

    // For standalone save: skip API call if nothing has changed
    if (!isSubmit && !hasDocumentationChanges()) {
      console.log("No changes to save");
      return false;
    }

    // Build save payload - with "Submitted" status for submit flow
    const savePayload = buildSavePayload(isSubmit ? "Submitted" : undefined);
    if (!savePayload) {
      console.error("Missing required data for save/submit");
      return false;
    }

    // TEMPORARY: User ID = selected partner from dropdown (listPartnerUser) when data-entry user and selection made; else logged-in user.
    const effectiveUserId =
      showDataEntryAndFooter && dataEntryDropdownSelected
        ? Number(dataEntryDropdownSelected.value)
        : loggedUserDetail?.ID;
    if (effectiveUserId != null) {
      savePayload.user = effectiveUserId;
    }

    // TEMPORARY: Add created_at from "Submitted on" datepicker for data-entry user. Remove with TEMPORARY block.
    const createdAtStr = showSubmittedOnDatePicker
      ? formatDataEntryDateToYYYYMMDD(dataEntrySubmittedOn)
      : undefined;
    if (createdAtStr) {
      savePayload.created_at = createdAtStr;
    }

    // For submit flow: ensure we include notes even if they haven't changed
    if (isSubmit) {
      const currentNotes = notes || "";
      const notesHtml = sanitizeNotesForApi(currentNotes);
      // TEMPORARY (data entry): data-entry users submit notes as partner_notes.
      if (isPartnerNotesUser) {
        savePayload.partner_notes = notesHtml;
      } else {
        savePayload.hid_notes = notesHtml;
      }
      savePayload.validation_status = "Submitted";
    }

    // Set loading state for toast notification tracking
    if (isSubmit) {
      setIsSubmitting(true);
    } else {
      setIsSaving(true);
    }

    // Dispatch appropriate action based on operation type
    if (isSubmit) {
      dispatch(
        submitValidationDocumentationSagaAction({
          integration_id: Number(integrationId),
          user: effectiveUserId ?? loggedUserDetail.ID,
          savePayload,
          ...(createdAtStr && { created_at: createdAtStr }),
        })
      );
    } else {
      dispatch(saveValidationDocumentationSagaAction(savePayload));
    }

    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
    return true;
  };

  // Validate that all verification statuses are set across all categories
  // Only applies to HID Admin users (skip for partner and for data-entry user - TEMPORARY)
  const validateVerificationStatuses = (): { isValid: boolean; pendingCategories: string[] } => {
    // Skip validation for Partner users - only HID Admin needs to verify
    // TEMPORARY: Also skip for data-entry user (kiran) - they don't have verification dropdown, so don't show this modal.
    if (!isHIDUser || showDataEntryAndFooter) {
      return { isValid: true, pendingCategories: [] };
    }

    if (!documentationData || documentationData.length === 0) {
      return { isValid: true, pendingCategories: [] };
    }

    const categoriesWithPendingStatus: string[] = [];

    documentationData.forEach((category) => {
      const categoryRequirements = category.requirements || [];
      let hasPendingStatus = false;

      categoryRequirements.forEach((req) => {
        const rowId = `req-${req.requirement_id}`;
        const formDataForRequirement = documentationFormData[rowId];
        
        // Check if verification status is set (either in form data or API data)
        const verificationStatus = formDataForRequirement?.verificationStatus || req.verification_status;
        
        // Verification status should be "Pass", "Failed", or "Not Applicable", not null, undefined, empty, or "Pending"
        const validVerificationStatuses = ["Pass", "Failed", "Not Applicable", "Completed"];
        if (!verificationStatus || 
            verificationStatus === "Pending" || 
            (typeof verificationStatus === "string" && verificationStatus.trim() === "") ||
            !validVerificationStatuses.includes(verificationStatus)) {
          hasPendingStatus = true;
        }
      });

      if (hasPendingStatus) {
        categoriesWithPendingStatus.push(category.category_name);
      }
    });

    return {
      isValid: categoriesWithPendingStatus.length === 0,
      pendingCategories: categoriesWithPendingStatus,
    };
  };

  const handleSave = () => {
    pushValidationDataToBackend(false);
  };

  const handleSubmit = () => {
    // Validate that all verification statuses are set before showing confirmation modal
    const validationResult = validateVerificationStatuses();
    if (!validationResult.isValid) {
      setPendingCategories(validationResult.pendingCategories);
      setIsValidationErrorModalOpen(true);
      return;
    }
    setIsConfirmationModalOpen(true);
  };

  const handleConfirmSubmit = async () => {
    // Double-check validation before submitting
    const validationResult = validateVerificationStatuses();
    if (!validationResult.isValid) {
      setPendingCategories(validationResult.pendingCategories);
      setIsConfirmationModalOpen(false);
      setIsValidationErrorModalOpen(true);
      return;
    }

    // For partner users, check environment details AFTER confirmation
    // This ensures the check happens even when there are unsaved changes
    if (!isHIDUser && integrationId) {
      try {
        // Determine which documentation_id to check
        // Use currentDocumentationId if available, otherwise use the latest from history
        let docIdToCheck: number | undefined = currentDocumentationId ?? undefined;
        
        // If currentDocumentationId is null (new version or unsaved changes), use the latest documentation_id from history
        if (docIdToCheck === undefined && historyData.length > 0) {
          docIdToCheck = historyData[0].documentation_id;
        }
        
        // We need a documentation_id to check environment details
        // If we don't have one, we can't verify environment exists, so show modal
        if (docIdToCheck === undefined) {
          setIsConfirmationModalOpen(false);
          setIsEnvironmentIncompleteModalOpen(true);
          return;
        }
        
        // Call environment API to check if environment details exist
        const { axiosRestInstance } = await import("../../../../../../../../common/axios");
        const searchParams = new URLSearchParams();
        searchParams.append("integration_id", String(integrationId));
        searchParams.append("documentation_id", String(docIdToCheck));
        
        const response = await axiosRestInstance.get(
          `/project/integration-validation/environment?${searchParams.toString()}`
        );
        
        const environmentResponse = response.data;
        
        // Show environment incomplete modal when:
        // - source is "none" (no environment exists)
        // - environment doesn't exist
        // - form_status is missing or not "Submitted" / "Verified"
        // Allow validation submission only when environment.form_status is "Submitted" or "Verified"
        // (If "Saved", partner must submit the environment form first; "Verified" is already verified by admin)
        const formStatus = environmentResponse?.environment?.form_status;
        const allowedStatuses = ["Submitted", "Verified"];
        
        if (
          environmentResponse?.source === "none" ||
          !environmentResponse?.environment ||
          !formStatus ||
          !allowedStatuses.includes(formStatus)
        ) {
          setIsConfirmationModalOpen(false);
          setIsEnvironmentIncompleteModalOpen(true);
          return;
        }
        
        // Environment exists with form_status "Submitted" or "Verified" - allow submission
      } catch (error) {
        console.error("Error checking environment details:", error);
        // If there's an error, proceed with submission (don't block on API error)
      }
    }

    // Close confirmation modal and proceed with submission
    setIsConfirmationModalOpen(false);
    pushValidationDataToBackend(true);
  };

  const handleHistoryClick = () => {
    navigate("history");
  };

  const handleCancel = () => {
    navigate(`/partner/${partnerID}/dashboard/projects/${projectName}`);
  };

  // Footer buttons: Partner - disable if Development not completed or when filling data; HID Admin enabled only when validation_status is "Submitted"
  // TEMPORARY: For data-entry user (kiran), enable Save/Submit when isDevelopmentCompleted. Otherwise normal rules.
  const shouldDisableFooterButtons = showDataEntryAndFooter
    ? !isDevelopmentCompleted
    : isHIDUser
      ? currentValidationStatus !== "Submitted"
      : !isDevelopmentCompleted;
  const submissionFooterDisabled =
    shouldDisableFooterButtons || submissionReadOnly || !canEditSubmission;

  // Disable table editing (dropdowns) when there's already a submitted history entry
  // (viewing a locked/submitted doc - must create new version to edit)
  const hasSubmittedHistory = useMemo(() => {
    return historyData.some(
      (entry) => entry.validation_status === "Submitted"
    );
  }, [historyData]);

  // Determine documentationId for summary: use currentDocumentationId if available,
  // otherwise fallback to latest documentation_id from historyData
  // This ensures we show previous summary data even after page refresh when currentDocumentationId is null
  const summaryDocumentationId = useMemo(() => {
    if (currentDocumentationId !== null && currentDocumentationId !== undefined) {
      return currentDocumentationId;
    }
    // Fallback to latest documentation_id from history if available
    if (historyData.length > 0) {
      return historyData[0].documentation_id;
    }
    return undefined;
  }, [currentDocumentationId, historyData]);

  // Use same resolved documentationId for environment modal so we always fetch latest
  // (e.g. after admin verification, partner refresh – modal shows Verified and disables buttons)
  const environmentModalDocumentationId = summaryDocumentationId;

  return (
    <div className="validation-tab-container">
      {/* Main Content Area - Sidebar + Documentation */}
      <div className="main-content-area">
        {/* Category Sidebar */}
        <div className="validation-sidebar">
          <h3 className="sidebar-title">Category</h3>
          <CategorySidebar
            categories={categories}
            activeCategory={activeCategory}
            onCategorySelect={handleCategorySelect}
          />
        </div>

        {/* Documentation Section */}
        <div className="documentation-section">
          <div className="documentation-header">
            <h2>Documentation</h2>
            {/* TEMPORARY: remove isDataEntryPartnerView prop when removing data-entry partner view */}
            <ActionButtons
              isHIDUser={isHIDUser}
              isDataEntryPartnerView={showDataEntryAndFooter}
              environmentActionDisabled={
                (!isHIDUser || showDataEntryAndFooter) &&
                (submissionReadOnly || !canEditSubmission)
              }
              onGuidanceClick={() => setIsGuidanceModalOpen(true)}
              onSummaryClick={() => setIsSummaryModalOpen(true)}
              onHistoryClick={handleHistoryClick}
              onEnvironmentDetailsClick={() => setIsEnvironmentModalOpen(true)}
            />
          </div>

          <div className="documentation-table-area">
            {/* 2026-03-10: onFormDataChange merges (prev + formData) so categories not visited during resubmit keep their seeded values. */}
            <DocumentationTable
              key={`${integrationId || "no-id"}-${
                currentDocumentationId != null ? currentDocumentationId : "new"
              }`}
              isHIDUser={isHIDUser}
              activeCategory={activeCategory}
              technology={documentationIds?.technologyId}
              integrationType={documentationIds?.integrationTypeId}
              integrationId={integrationId ? Number(integrationId) : undefined}
              onFormDataChange={(formData) =>
                setDocumentationFormData((prev) => ({ ...prev, ...formData }))
              }
              parentFormData={documentationFormData}
              disableEditing={hasSubmittedHistory}
              canEditSubmission={!submissionFieldsLocked}
              canEditApproval={!approvalFieldsLocked}
              recentHistoryDocumentation={recentHistoryDocumentation}
              isNewVersion={currentDocumentationId === null}
            />
          </div>
        </div>
      </div>

      {/* Notes Section */}
      <NotesSection
        notes={notes}
        onChange={setNotes}
        readOnly={notesReadOnly}
      />

  {/* ========== TEMPORARY: Data entry block - only for DATA_ENTRY_PARTNER_VIEW_EMAIL (remove when not needed) ========== */}
  {showSubmittedOnDatePicker && (
    <div className="validation-data-entry-block">
      <div className="data-entry-row">
        {showDataEntryAndFooter && (
          <div className="data-entry-field">
            <label className="data-entry-label">Partner Name</label>
            {/* TEMPORARY: options from GET /user/listPartnerUser/:partnerId. Remove with TEMPORARY block. */}
            <Dropdown
              id="data-entry-dropdown"
              label=""
              options={dataEntryPartnerOptions}
              selected={dataEntryDropdownSelected}
              onSelect={(opt) => setDataEntryDropdownSelected(opt ?? null)}
              placeholder={dataEntryPartnerOptionsLoading ? "Loading..." : "Select..."}
              theme="dark"
              disabled={dataEntryPartnerOptionsLoading || submissionFieldsLocked}
              isDisabled={dataEntryPartnerOptionsLoading || submissionFieldsLocked}
            />
          </div>
        )}
        <div className="data-entry-field">
          <label className="data-entry-label">Submitted on</label>
          {/* TEMPORARY: format YYYY-MM-DD to match API created_at. Remove with TEMPORARY block. */}
          <DatePicker
            id="data-entry-submitted-on"
            name="dataEntrySubmittedOn"
            label=""
            format="dd-MM-yyyy"
            placeholder="dd-mm-yyyy"
            theme="dark"
            value={dataEntrySubmittedOn}
            disabled={submittedOnLocked}
            isDisabled={submittedOnLocked}
            onChange={(date) => {
              if (submittedOnLocked) return;
              setDataEntrySubmittedOn(
                date ? (Array.isArray(date) ? date[0] : date) : null
              );
            }}
          />
        </div>
      </div>
    </div>
  )}
  {/* ========== TEMPORARY: Data entry block end ========== */}

      {/* Files Section */}
      <FilesSection
        isHIDUser={isHIDUser}
        integrationId={integrationId}
        allowFileMutations={
            showDataEntryAndFooter
            ? canEditSubmission && !submissionReadOnly
            : isHIDUser
              ? canEditApproval && !approvalReadOnly
              : canEditSubmission && !submissionReadOnly
        }
        // TEMPORARY: pass data-entry context so upload API can use Partner Name + Submitted on.
        isDataEntryUserView={showDataEntryAndFooter}
        dataEntryUploadedByUserId={
          showDataEntryAndFooter && dataEntryDropdownSelected
            ? Number(dataEntryDropdownSelected.value)
            : undefined
        }
        dataEntryCreatedAt={
          showDataEntryAndFooter
            ? formatDataEntryDateToYYYYMMDD(dataEntrySubmittedOn)
            : undefined
        }
      />

      {/* Footer Actions - hidden for HID admin on validation screen; shown on validation history page */}
      {/* TEMPORARY: Also show footer for data-entry email (partner view authority). Revert to: {!isHIDUser && ( */}
      {(!isHIDUser || showDataEntryAndFooter) && canAccessSubmission && (
    <div className="validation-footer">
      <Button
        variant="flat"
        label="Cancel"
        normalCase={true}
        onClick={handleCancel}
      />
      <div className="footer-right-actions">
        <Button
          variant="flat"
          label="Save"
          normalCase={true}
          onClick={handleSave}
          disabled={submissionFooterDisabled}
        />
        <Button
          variant="primary"
          label="Submit for validation"
          normalCase={true}
          onClick={handleSubmit}
          disabled={submissionFooterDisabled}
        />
      </div>
    </div>
      )}

      {isGuidanceModalOpen && (
        <GuidanceModal
          onClose={() => setIsGuidanceModalOpen(false)}
          technology={technology}
          integration={integrationName}
        />
      )}

      {isSummaryModalOpen && (
        <Modal
          headerText="Summary"
          width={900}
          onClose={() => setIsSummaryModalOpen(false)}
          className="summary-modal"
        >
          <ValidationSummary 
            integrationId={integrationId ? Number(integrationId) : undefined} 
            documentationId={summaryDocumentationId}
          />
        </Modal>
      )}

      {isEnvironmentModalOpen && technology && integrationName && integrationId && (
        <EnvironmentDetailsModal
          isHIDUser={isHIDUser}
          onClose={() => setIsEnvironmentModalOpen(false)}
          technology={technology}
          integration={integrationName}
          integrationId={Number(integrationId)}
          documentationId={environmentModalDocumentationId}
          existingData={integrationData}
          disableSaveSubmit={
            !isDevelopmentCompleted ||
            submissionReadOnly ||
            !canEditSubmission
          }
          validationStatus={currentValidationStatus}
          skipProjectDetailsRefetch={true}
        />
      )}

      {isConfirmationModalOpen && (
        <ConfirmationModal
          title="Confirmation required"
          message="Please confirm that you want to submit for validation."
          confirmLabel="Yes, submit"
          cancelLabel="Cancel"
          onConfirm={handleConfirmSubmit}
          onCancel={() => setIsConfirmationModalOpen(false)}
        />
      )}

      {isValidationErrorModalOpen && (
        <ValidationErrorModal
          title="Verification Status Pending"
          message="Verification status is pending from the following category(ies). Please complete all verification statuses before submitting."
          pendingCategories={pendingCategories}
          onClose={() => setIsValidationErrorModalOpen(false)}
        />
      )}

      {isEnvironmentIncompleteModalOpen && (
        <Modal
          headerText="Environment Details Required"
          width={500}
          onClose={() => setIsEnvironmentIncompleteModalOpen(false)}
          className="confirmation-modal"
        >
          <div className="modal-content">
            <p className="confirmation-message">Please complete environment details.</p>
            <div className="modal-footer centered">
              <Button
                variant="primary"
                label="OK"
                normalCase={true}
                onClick={() => {
                  setIsEnvironmentIncompleteModalOpen(false);
                  setIsEnvironmentModalOpen(true);
                }}
              />
            </div>
          </div>
        </Modal>
      )}

      <Notification toasts={toasts} setToasts={setToasts} />
    </div>
  );
};

export default ValidationTab;

